import React, { FormEvent, useState } from 'react';
import { Element } from 'react-scroll';
import { useAsync } from 'react-use';
import { always, isEmpty } from 'ramda';
import { FormattedMessage, useIntl } from 'react-intl';

import { ButtonContainer, ContinueReadingButton } from '@components/continueReadingButtonArtist';
import { JumpToSection as JumpToSectionButton } from '@components/jumptoSectionButtons';
import { ContentBody } from '@components/contentBody';
import { ArtistPage } from '@definitions/artists.types';
import { Block } from '@definitions/common.types';
import {
  NewsletterBlock,
  NoArtworks,
  ParagraphWithTitle,
  PARAGRAPH_WITH_TITLE_OPTION,
  ProductsBlock,
} from '@components/blocks';
import { useMonogram } from '@hooks/useMonogram';
import { renderWhenTrue, renderWhenTrueOtherwise } from '@utils/rendering';
import { GalleryRepresentation } from '@components/galleryRepresentation';
import { capitalizeWords, getHasProducts } from '@utils/helpers';
import { NotifyForm } from '@components/notifyForm';
import { useTrackConversions } from '@hooks/useTrackConversions';
import { useCustomer } from '@hooks/useCustomer';
import { CONVERSIONS_EVENTS, VIEW_CONTENT_TYPES } from '@definitions/conversions.types';
import api from '@services/nextAPI';
import { useBreakpoint } from '@hooks/useBreakpoint';

import {
  Container,
  ProductsContainer,
  ContainerNarrow,
  HorizontalLine,
  ArtistTitle,
  ArtistContainer,
  Title,
  ContentBodyWrapper,
} from './artistView.styles';

export const CONTENT_BODY_ELEMENT_NAME = 'artist-body';
export const CONTENT_INTRODCTION_ELEMENT_NAME = 'Introduction';
export const CONTENT_ARTWORKS_ELEMENT_NAME = 'Artworks';
export const CONTENT_GALLERY_ELEMENT_NAME = 'Gallery';
export const CONTENT_BIO_ELEMENT_NAME = 'Biography';

export interface ArtistViewProps {
  artistPage: ArtistPage;
}
export interface ProductObject {
  type: string;
  value: any[];
}
export const ArtistView = ({ artistPage }: ArtistViewProps) => {
  const { isMobile } = useBreakpoint();
  const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);
  const [isContinuReadingClicked, setIsContinuReadingClicked] = useState(false);
  const { customer } = useCustomer();
  const { addMonogram, clearMonogram } = useMonogram();
  const { sendConversion } = useTrackConversions();
  const hasProducts = artistPage?.artistProducts ? getHasProducts(artistPage.artistProducts) : false;
  const paragraphTextCount = artistPage?.biography ? artistPage?.biography[0].value.paragraph.length : 0;

  const intl = useIntl();

  const newsletterSuccessInformation = intl.formatMessage({
    id: 'artistView.newsletterSuccessInformation',
    defaultMessage: "Thank you! You've successfully subscribed to the artist newsletter.",
  });
  const newsletterTitle = intl.formatMessage({
    id: 'artistView.newsletterTitle',
    defaultMessage: 'Newsletter:',
  });
  const newsletterInfoText = intl.formatMessage(
    {
      id: 'artistView.newsletterInfoText',
      defaultMessage: 'Sign up for updates about {artistName} and other artists.',
    },
    { artistName: capitalizeWords(artistPage.title) }
  );

  const handleNotifySubmit = async (e: FormEvent, formData: { email: string; firstName: string; lastName: string }) => {
    e.preventDefault();
    const { email, firstName, lastName } = formData;
    await api.post('email/subscribe', {
      email,
      firstName,
      lastName,
      tags: [`Artist page ID ${artistPage.id}`],
    });
    setIsConfirmationVisible(true);
  };

  useAsync(async () => {
    if (
      artistPage.artistProducts &&
      artistPage.artistProducts.length &&
      artistPage.pastArtistProducts &&
      artistPage.pastArtistProducts.length
    ) {
      artistPage.artistProducts[0].value = [
        ...artistPage.artistProducts[0].value,
        ...artistPage.pastArtistProducts[0].value,
      ];
    }
    const id = new Date().getTime();

    if (artistPage.monogram?.monogramLetter?.length) {
      const { monogramLetter, monogramColor, monogramAnimationType } = artistPage.monogram;

      addMonogram({
        letter: monogramLetter,
        color: monogramColor,
        appearance: monogramAnimationType,
        id,
      });
    }

    await sendConversion({
      eventType: CONVERSIONS_EVENTS.VIEW_CONTENT,
      contentName: VIEW_CONTENT_TYPES.ARTIST_PLP,
    });

    return () => {
      clearMonogram(id);
    };
  }, [artistPage]);

  const renderBiography = (biography: Block, index: number) => {
    biography.value.title = '';
    return <ParagraphWithTitle key={index} {...biography.value} isContinuReadingClicked={isContinuReadingClicked} />;
  };

  const renderGalleryRepresentationBlock = renderWhenTrue(() => (
    <GalleryRepresentation galleries={artistPage.representedBy} />
  ));

  const renderProducts = (artistProducts: Block, index: number) => (
    <ProductsContainer key={index}>
      <ProductsBlock artworks={artistProducts.value} hasSpacingTop={false} />
    </ProductsContainer>
  );

  const renderArtistProducts = renderWhenTrue(
    always(artistPage.artistProducts ? artistPage.artistProducts.map(renderProducts) : null)
  );

  const renderNoArtworks = renderWhenTrue(always(<NoArtworks />));

  const renderContinueReadingButton = renderWhenTrue(
    always(
      <a
        onClick={() => {
          setIsContinuReadingClicked(!isContinuReadingClicked);
        }}
      >
        <ButtonContainer>
          <ContinueReadingButton isContinuReadingClicked={isContinuReadingClicked} />
        </ButtonContainer>
      </a>
    )
  );

  const renderReducedView = always(
    <>
      <ParagraphWithTitle
        title={artistPage.title}
        paragraph="This artist is coming soon to Platform. Sign up for our email list to be notified when their artworks become available."
        displayOption={PARAGRAPH_WITH_TITLE_OPTION.BIOGRAPHY}
        isContinuReadingClicked={isContinuReadingClicked}
      />
      <ContainerNarrow>
        <NotifyForm
          onFormSubmit={(e, { email, firstName, lastName }) => handleNotifySubmit(e, { email, firstName, lastName })}
          isConfirmationVisible={isConfirmationVisible}
          texts={{
            info: '',
            confirmation: 'You will be notified when the artist becomes available.',
            submit: 'SIGN UP',
          }}
          initialFields={{
            email: customer?.email,
            firstName: customer?.firstName,
            lastName: customer?.lastName,
          }}
        />
      </ContainerNarrow>
    </>
  );

  const renderButtons = always(
    <div style={{ marginLeft: 30 }}>
      <Title style={{ marginBottom: 20, marginLeft: -15 }}>
        <FormattedMessage id="artistView.artist" defaultMessage="Artist:" />
      </Title>
      <JumpToSectionButton elementName={CONTENT_INTRODCTION_ELEMENT_NAME} />
      <JumpToSectionButton elementName={CONTENT_ARTWORKS_ELEMENT_NAME} />
      <JumpToSectionButton elementName={CONTENT_BIO_ELEMENT_NAME} />
      <JumpToSectionButton elementName={CONTENT_GALLERY_ELEMENT_NAME} />
    </div>
  );

  const renderFullView = always(
    <>
      <ArtistContainer>
        {!isMobile && renderButtons()}
        <ArtistTitle>
          {isMobile && (
            <Title>
              <FormattedMessage id="artistView.artist" defaultMessage="Artist:" />
            </Title>
          )}
          <div style={{ textAlign: 'center' }}>
            <FormattedMessage id="artistView.name" defaultMessage={capitalizeWords(artistPage.title)} />
          </div>
          <Element name={CONTENT_INTRODCTION_ELEMENT_NAME}>
            {artistPage?.biography?.map(renderBiography)}
            {paragraphTextCount > 256 && renderContinueReadingButton(artistPage.showContinuedReading)}
          </Element>
        </ArtistTitle>
      </ArtistContainer>
      <HorizontalLine />
      {isMobile ? (
        <Title>
          <FormattedMessage id="artistView.artworks" defaultMessage="Artworks:" />
        </Title>
      ) : (
        <Title>
          <FormattedMessage id="artistView.artworks" defaultMessage="Artworks:" />
        </Title>
      )}

      <Element name={CONTENT_ARTWORKS_ELEMENT_NAME}>
        {renderArtistProducts(hasProducts)}
        {renderNoArtworks(!hasProducts)}
      </Element>

      <Element name={CONTENT_BIO_ELEMENT_NAME}>
        {artistPage.body && artistPage.body.length > 0 && (
          <>
            <HorizontalLine />
            <Title>
              <FormattedMessage id="artistView.biography" defaultMessage="Biography:" />
            </Title>
            <ContentBodyWrapper>
              <ContentBody hasSpacingTop={false} hasSpacingBottom={false} body={artistPage.body}></ContentBody>
            </ContentBodyWrapper>
          </>
        )}
      </Element>
      <HorizontalLine style={{ marginTop: 30 }} />

      <Element name={CONTENT_GALLERY_ELEMENT_NAME}>
        {renderGalleryRepresentationBlock(!isEmpty(artistPage.representedBy))}
      </Element>
      <HorizontalLine />
      <NewsletterBlock
        infoText={newsletterInfoText}
        successInformation={newsletterSuccessInformation}
        tag={artistPage.title}
        title={newsletterTitle}
      />
    </>
  );

  const renderView = renderWhenTrueOtherwise(renderReducedView, renderFullView);

  return <Container>{renderView(artistPage.reducedVersion)}</Container>;
};
